import { useState, type FormEvent } from 'react';
import { useNavigate } from 'react-router-dom';
import { useIdentity } from '../../identity/state/useIdentity';
import { friendlyErrorMessage } from '../../../shared/friendlyError';

const MIN_LENGTH = 8;

function clientSideComplexityError(password: string): string | null {
  if (password.length < MIN_LENGTH) return `Must be at least ${MIN_LENGTH} characters.`;
  if (!/[A-Z]/.test(password)) return 'Must include an uppercase letter.';
  if (!/[a-z]/.test(password)) return 'Must include a lowercase letter.';
  if (!/[0-9]/.test(password)) return 'Must include a digit.';
  if (!/[^A-Za-z0-9]/.test(password)) return 'Must include a special character.';
  return null;
}

/**
 * Real, working — not a placeholder. Success revokes every session the user holds (including this one;
 * see IdentityProvider.changePassword), so this always ends in a redirect to /login, never a "saved" toast
 * that leaves the user on a now-invalid session.
 */
export function ChangePasswordPage() {
  const { changePassword } = useIdentity();
  const navigate = useNavigate();
  const [currentPassword, setCurrentPassword] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [fieldError, setFieldError] = useState<string | null>(null);
  const [submitError, setSubmitError] = useState<string | null>(null);
  const [submitting, setSubmitting] = useState(false);

  async function handleSubmit(e: FormEvent) {
    e.preventDefault();
    setSubmitError(null);

    if (newPassword !== confirmPassword) {
      setFieldError('New password and confirmation do not match.');
      return;
    }
    const complexityError = clientSideComplexityError(newPassword);
    if (complexityError) {
      setFieldError(complexityError);
      return;
    }
    setFieldError(null);

    setSubmitting(true);
    try {
      await changePassword(currentPassword, newPassword);
      navigate('/login', { replace: true });
    } catch (err) {
      setSubmitError(friendlyErrorMessage(err, 'Could not change your password.'));
    } finally {
      setSubmitting(false);
    }
  }

  return (
    <section className="card card--wide">
      <h1>Change Password</h1>
      <p className="muted">
        Changing your password signs out every other device and browser session — you will need to sign
        in again here too.
      </p>
      <form className="inline-form" onSubmit={handleSubmit} style={{ flexDirection: 'column', alignItems: 'stretch', maxWidth: 360 }}>
        <label htmlFor="current-password">Current password</label>
        <input
          id="current-password"
          type="password"
          value={currentPassword}
          onChange={(e) => setCurrentPassword(e.target.value)}
          autoComplete="current-password"
          required
        />

        <label htmlFor="new-password">New password</label>
        <input
          id="new-password"
          type="password"
          value={newPassword}
          onChange={(e) => setNewPassword(e.target.value)}
          autoComplete="new-password"
          required
        />

        <label htmlFor="confirm-password">Confirm new password</label>
        <input
          id="confirm-password"
          type="password"
          value={confirmPassword}
          onChange={(e) => setConfirmPassword(e.target.value)}
          autoComplete="new-password"
          required
        />

        {fieldError && <p className="error">{fieldError}</p>}
        {submitError && <p className="error">{submitError}</p>}

        <button type="submit" disabled={submitting}>
          {submitting ? 'Changing password…' : 'Change Password'}
        </button>
      </form>
    </section>
  );
}
